import React from 'react';
import { Clock, ChevronDown, Undo } from 'lucide-react';
import { Task, TaskStatus } from '../../types/todo';
import { TaskPriority } from '../TaskPriority';
import { TaskStatus as TaskStatusComponent } from '../TaskStatus';

interface TaskHeaderProps {
  task: Task;
  isPreview: boolean;
  isCollapsed: boolean;
  onToggleCollapse: (e: React.MouseEvent) => void;
  daysRemaining: number | null;
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
  onUpdatePriority: (taskId: string, priority: string) => void;
}

export function TaskHeader({
  task,
  isPreview,
  isCollapsed,
  onToggleCollapse,
  daysRemaining,
  onUpdateStatus,
  onUpdatePriority
}: TaskHeaderProps) {
  const [showUndoButton, setShowUndoButton] = React.useState(false);
  const [previousStatus, setPreviousStatus] = React.useState<TaskStatus | null>(null);
  const undoTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  const handleStatusClick = () => {
    if (isPreview) return;
    setPreviousStatus(task.status);
    const nextStatus: TaskStatus =
      task.status === 'not-started' ? 'in-progress' :
      task.status === 'in-progress' ? 'completed' : 'not-started';
    onUpdateStatus(task.id, nextStatus);
    setShowUndoButton(true);
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }
    undoTimeoutRef.current = setTimeout(() => {
      setShowUndoButton(false);
      setPreviousStatus(null);
    }, 2000);
  };

  const handleUndo = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (previousStatus !== null) {
      onUpdateStatus(task.id, previousStatus);
    }
    setShowUndoButton(false);
    setPreviousStatus(null);
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
  };

  const handlePriorityClick = () => {
    if (isPreview) return;
    const nextPriority =
      task.priority === 'low' ? 'medium' :
      task.priority === 'medium' ? 'high' : 'low';
    onUpdatePriority(task.id, nextPriority);
  };

  const getDaysRemainingStyles = (days: number) => {
    if (days <= 7) return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400';
    if (days <= 30) return 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400';
    return 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400';
  };

  const hasDetails = task.description || task.subtasks.length > 0 || task.notes;

  return (
    <div>
      <div className="flex items-start gap-2.5 sm:gap-3">
        <div className="relative flex-shrink-0">
          <button
            onClick={handleStatusClick}
            disabled={isPreview}
            className="cursor-pointer touch-manipulation"
          >
            <TaskStatusComponent status={task.status} isPreview={isPreview} />
          </button>
          {showUndoButton && !isPreview && (
            <button
              onClick={handleUndo}
              className="absolute -bottom-9 left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 sm:px-2 sm:py-0.5 text-xs sm:text-[10px] font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-lg sm:rounded-md border border-amber-200 dark:border-amber-800/30 shadow-md sm:shadow-sm hover:bg-amber-100 dark:hover:bg-amber-800/30 transition-colors animate-fade-in z-10 touch-manipulation"
            >
              <Undo className="w-3.5 h-3.5 sm:w-2.5 sm:h-2.5" />
              <span>Annuler</span>
            </button>
          )}
        </div>

        <div className="flex-1 min-w-0 pt-1.5 sm:pt-0">
          <div className="flex items-start gap-2 sm:gap-2 flex-wrap">
            <span className={`text-[15px] sm:text-base font-medium text-gray-900 dark:text-white leading-snug ${
              task.status === 'completed' ? 'line-through text-gray-500 dark:text-gray-400' : ''
            }`}>
              {task.title}
              {task.subtasks.length > 0 && (
                <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500 font-normal">
                  ({task.subtasks.filter(st => st.completed).length}/{task.subtasks.length})
                </span>
              )}
            </span>
            <div className="flex items-center gap-1.5 sm:gap-1 flex-shrink-0">
              <TaskPriority
                priority={task.priority}
                isPreview={isPreview}
                onClick={handlePriorityClick}
              />
              {daysRemaining !== null && daysRemaining > 0 && (
                <div className={`flex items-center gap-1 px-2 py-1 sm:px-1.5 sm:py-0.5 rounded-full text-xs sm:text-[10px] font-medium ${getDaysRemainingStyles(daysRemaining)}`}>
                  <Clock className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
                  <span>J-{daysRemaining}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {hasDetails && (
          <button
            onClick={onToggleCollapse}
            className="flex-shrink-0 p-2.5 sm:p-1.5 -mr-1 rounded-xl sm:rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors touch-manipulation"
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Afficher les détails" : "Masquer les détails"}
          >
            <ChevronDown className={`w-5 h-5 sm:w-4 sm:h-4 transition-transform duration-200 ${
              isCollapsed ? '' : 'rotate-180'
            }`} />
          </button>
        )}
      </div>

      {task.description && !isCollapsed && (
        <div className="mt-3 sm:mt-3 pl-9 sm:pl-[calc(theme(spacing.3)+theme(spacing.10))]">
          <div
            className="task-description text-sm sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: task.description }}
          />
        </div>
      )}
    </div>
  );
}
